// src/components/PdfViewerDialog.jsx
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  IconButton, Typography, Box, Button
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const PdfViewerDialog = ({ open, src, fileName, onClose }) => {
  const canvasRef   = React.useRef(null);
  const pdfRef      = React.useRef(null);     // loaded PDF instance
  const taskRef     = React.useRef(null);     // pdfjs loading task
  const renderRef   = React.useRef(null);     // current page render task
  const cancelled   = React.useRef(false);

  const [numPages, setNumPages] = React.useState(0);
  const [page, setPage]         = React.useState(1);
  const [scale, setScale]       = React.useState(1);
  const [busy, setBusy]         = React.useState(false);
  const [err, setErr]           = React.useState('');

  const [attempt, setAttempt]     = React.useState(0);
  const [autoTried, setAutoTried] = React.useState(false);
  const [pdfVersion, setPdfVersion] = React.useState(0); // bump to signal new pdf loaded

  const withCacheBuster = React.useCallback((u) => {
    try {
      const url = new URL(u, window.location.href);
      url.searchParams.set('_cb', String(Date.now()));
      return url.toString();
    } catch {
      const sep = u.includes('?') ? '&' : '?';
      return `${u}${sep}_cb=${Date.now()}`;
    }
  }, []);

  const cancelRender = React.useCallback(() => {
    try { renderRef.current?.cancel?.(); } catch {}
    renderRef.current = null;
  }, []);

  const destroyTask = React.useCallback(async () => {
    try { await taskRef.current?.destroy?.(); } catch {}
    taskRef.current = null;
  }, []);

  const destroyPdf = React.useCallback(async () => {
    try { await pdfRef.current?.destroy?.(); } catch {}
    pdfRef.current = null;
  }, []);

  const renderPage = React.useCallback(async (pageNum, scl) => {
    const canvas = canvasRef.current;
    const pdf    = pdfRef.current;
    if (!canvas || !pdf) return;

    setBusy(true);
    cancelRender(); // ensure prior render is cancelled

    try {
      const p = await pdf.getPage(pageNum);
      const viewport = p.getViewport({ scale: scl });
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d', { alpha: false });

      canvas.width  = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width  = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const task = p.render({ canvasContext: ctx, viewport });
      renderRef.current = task;
      await task.promise; // will throw RenderingCancelledException if superseded
    } catch (e) {
      // Silently ignore expected cancellations
      if (e?.name !== 'RenderingCancelledException') {
        // Optional: console.warn('PDF render error:', e);
      }
    } finally {
      setBusy(false);
    }
  }, [cancelRender]);

  const loadPdf = React.useCallback(async ({ useBuster = false } = {}) => {
    if (!open || !src) return;
    cancelled.current = false;
    setBusy(true);
    setErr('');

    cancelRender();
    await destroyTask();
    await destroyPdf();

    const url = useBuster ? withCacheBuster(src) : src;

    try {
      const task = getDocument({ url, withCredentials: false });
      taskRef.current = task;

      const pdf = await task.promise;
      if (cancelled.current) return;

      pdfRef.current = pdf;
      setNumPages(pdf.numPages);
      setPage(1);
      setScale(1);
      setPdfVersion(v => v + 1); // signal that a new pdf instance is ready
    } catch (e) {
      if (!cancelled.current) setErr('Could not load PDF.');
    } finally {
      setBusy(false);
    }
  }, [cancelRender, destroyPdf, destroyTask, open, src, withCacheBuster]);

  // Initial load / src change / manual attempts
  React.useEffect(() => {
    if (!open || !src) return;
    setAutoTried(false);
    loadPdf({ useBuster: false });
    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, src, attempt]);

  // Auto-retry once with cache-buster
  React.useEffect(() => {
    if (!open || !src) return;
    if (!err || autoTried) return;
    const t = setTimeout(() => {
      setAutoTried(true);
      loadPdf({ useBuster: true });
    }, 500);
    return () => clearTimeout(t);
  }, [autoTried, err, loadPdf, open, src]);

  // Single render path:
  // - when a new pdf is loaded (pdfVersion changes)
  // - or when page/scale change (and a pdf exists)
  React.useEffect(() => {
    if (!open || !src || !pdfRef.current) return;
    renderPage(page, scale);
  }, [open, src, page, scale, pdfVersion, renderPage]);

  // Cleanup on unmount/close
  React.useEffect(() => {
    return () => {
      cancelled.current = true;
      cancelRender();
      destroyTask();
      destroyPdf();
    };
  }, [cancelRender, destroyPdf, destroyTask]);

  const zoomIn  = () => setScale(s => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.25));
  const prev    = () => setPage(p => Math.max(p - 1, 1));
  const next    = () => setPage(p => Math.min(p + 1, numPages));

  const manualRetry = () => {
    setAutoTried(false);
    setErr('');
    setAttempt(n => n + 1);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {fileName || 'Preview'}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={zoomOut} size="small" disabled={busy || !!err} title="Zoom out"><ZoomOutIcon fontSize="small" /></IconButton>
          <IconButton onClick={zoomIn}  size="small" disabled={busy || !!err} title="Zoom in"><ZoomInIcon fontSize="small" /></IconButton>
          <IconButton onClick={prev}    size="small" disabled={busy || !!err || page<=1} title="Previous"><NavigateBeforeIcon fontSize="small" /></IconButton>
          <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
            {numPages ? `${page} / ${numPages}` : '—'}
          </Typography>
          <IconButton onClick={next}    size="small" disabled={busy || !!err || page>=numPages} title="Next"><NavigateNextIcon fontSize="small" /></IconButton>
          <IconButton size="small" title="Open original" onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose} size="small" title="Close"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'grid', placeItems: 'center', bgcolor: '#f7f7f7', minHeight: 420 }}>
        {err ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography color="error" sx={{ mb: 1 }}>{err}</Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button variant="contained" size="small" onClick={manualRetry} disabled={busy}>Retry</Button>
              <Button variant="outlined" size="small" onClick={() => loadPdf({ useBuster: true })} disabled={busy}>
                Retry (Force refresh)
              </Button>
              <Button variant="text" size="small" onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}>
                Open original
              </Button>
            </Box>
          </Box>
        ) : (
          <canvas ref={canvasRef} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerDialog;
